import { Button } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import {
  defaultWorkspaceApiClient,
  SubscriptionService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { formatPrice } from '@affine/core/utils/format-price';
import { SubscriptionRecurring } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CancelTeamAction,
  ContractChangeWithdrawalAction,
  WithdrawalAction,
} from './actions';
import { CardNameLabelRow } from './card-name-label-row';
import * as styles from './styles.css';

export const TeamCard = () => {
  const t = useI18n();
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const workspaceQuotaService = useService(WorkspaceQuotaService);
  const subscriptionService = useService(SubscriptionService);
  const workspace = useService(WorkspaceService).workspace;
  const workspaceQuota = useLiveData(workspaceQuotaService.quota.quota$);
  const workspaceMemberCount = workspaceQuota?.memberCount;
  const teamSubscription = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );
  // sl-8m4o.1: Load price for the ACTUAL plan, not hardcoded teamPrice$
  const allPrices = useLiveData(subscriptionService.prices.prices$);
  const teamPrices = useMemo(
    () => allPrices?.find(p => p.plan === teamSubscription?.plan) ?? null,
    [allPrices, teamSubscription?.plan]
  );

  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openWithdrawalModal, setOpenWithdrawalModal] = useState(false);
  const [openChangeWithdrawalId, setOpenChangeWithdrawalId] = useState<
    string | null
  >(null);
  const [contractChanges, setContractChanges] = useState<
    BillingContractChange[]
  >([]);

  const loadContractChanges = useCallback(async () => {
    const path = `/api/payment/legal/contract-changes?workspaceId=${encodeURIComponent(
      workspace.id
    )}`;
    const response = await defaultWorkspaceApiClient.request({
      workspaceId: workspace.id,
      path,
      category: 'settings',
      key: `GET:${path}`,
      mode: 'background',
      priority: 'visible',
    });
    if (!response.ok) {
      return;
    }
    const body = (await response.json()) as {
      changes?: BillingContractChange[];
    };
    setContractChanges(body.changes ?? []);
  }, [workspace.id]);

  useEffect(() => {
    workspaceSubscriptionService.subscription.revalidate();
    workspaceQuotaService.quota.revalidate();
    subscriptionService.prices.revalidate();
    void loadContractChanges();
  }, [
    loadContractChanges,
    subscriptionService,
    workspaceQuotaService,
    workspaceSubscriptionService,
  ]);

  const expiration = teamSubscription?.canceledAt;
  const nextBillingDate = teamSubscription?.nextBillAt;
  const recurring = teamSubscription?.recurring;
  const endDate = teamSubscription?.end;
  const canWithdraw = teamSubscription?.canWithdraw === true;

  // sl-8m4o.1: Description based on actual recurring interval, no "free trail" fallback.
  // Trial state should check trialEnd date, not be a fallback for unknown recurring.
  const description = useMemo(() => {
    if (recurring === SubscriptionRecurring.Yearly) {
      return t[
        'com.streamlain.settings.workspace.billing.team-workspace.description.billed.annually'
      ]();
    }
    if (recurring === SubscriptionRecurring.Monthly) {
      return t[
        'com.streamlain.settings.workspace.billing.team-workspace.description.billed.monthly'
      ]();
    }
    return '';
  }, [recurring, t]);

  const expirationDate = useMemo(() => {
    if (expiration && endDate) {
      return t[
        'com.streamlain.settings.workspace.billing.team-workspace.not-renewed'
      ]({
        date: new Date(endDate).toLocaleDateString(),
      });
    }
    if (nextBillingDate && endDate) {
      return t[
        'com.streamlain.settings.workspace.billing.team-workspace.next-billing-date'
      ]({
        date: new Date(endDate).toLocaleDateString(),
      });
    }
    return '';
  }, [endDate, expiration, nextBillingDate, t]);

  const amountCents = teamSubscription
    ? teamPrices && workspaceMemberCount
      ? teamSubscription.recurring === SubscriptionRecurring.Monthly
        ? (teamPrices.amount ?? 0) * workspaceMemberCount
        : (teamPrices.yearlyAmount ?? 0) * workspaceMemberCount
      : null
    : 0;
  const currency = teamPrices?.currency ?? 'eur';
  const handleClick = useCallback(() => {
    setOpenCancelModal(true);
  }, []);
  const handleWithdrawalClick = useCallback(() => {
    setOpenWithdrawalModal(true);
  }, []);

  return (
    <div className={styles.planCard}>
      <div className={styles.currentPlan}>
        <SettingRow
          spreadCol={false}
          name={
            <CardNameLabelRow
              cardName={t[
                'com.streamlain.payment.billing-setting.plan-card-title'
              ]({
                plan: teamSubscription?.plan ?? 'Subscription',
              })}
              status={teamSubscription?.status}
              canceledAt={teamSubscription?.canceledAt}
            />
          }
          desc={
            <>
              <div>{description}</div>
              <div>{expirationDate}</div>
            </>
          }
        />
        <CancelTeamAction
          open={openCancelModal}
          onOpenChange={setOpenCancelModal}
        >
          <Button
            variant="secondary"
            className={styles.cancelPlanButton}
            data-testid="subscription-cancel-button"
            onClick={handleClick}
          >
            {t[
              'com.streamlain.settings.workspace.billing.team-workspace.cancel-plan'
            ]()}
          </Button>
        </CancelTeamAction>
        {canWithdraw ? (
          <WithdrawalAction
            open={openWithdrawalModal}
            onOpenChange={setOpenWithdrawalModal}
          >
            <Button
              variant="plain"
              className={styles.cancelPlanButton}
              data-testid="subscription-withdrawal-button"
              onClick={handleWithdrawalClick}
            >
              {t['com.streamlain.payment.withdrawal.entry']()}
            </Button>
          </WithdrawalAction>
        ) : null}
        {contractChanges.map(change => {
          const label = getContractChangeLabel(change, t.language);
          return (
            <ContractChangeWithdrawalAction
              changeId={change.id}
              contractLabel={label}
              key={change.id}
              onOpenChange={open => {
                setOpenChangeWithdrawalId(open ? change.id : null);
              }}
              onSubmitted={loadContractChanges}
              open={openChangeWithdrawalId === change.id}
            >
              <Button
                variant="plain"
                className={styles.cancelPlanButton}
                data-testid={`contract-change-withdrawal-${change.targetType}-${change.id}`}
                onClick={() => {
                  setOpenChangeWithdrawalId(change.id);
                }}
              >
                {t['com.streamlain.payment.withdrawal.entry']()}
              </Button>
            </ContractChangeWithdrawalAction>
          );
        })}
      </div>
      <p className={styles.planPrice}>
        {amountCents !== null ? formatPrice(amountCents, currency) : '?'}
        <span className={styles.billingFrequency}>
          /
          {teamSubscription?.recurring === SubscriptionRecurring.Monthly
            ? t['com.streamlain.payment.billing-setting.month']()
            : t['com.streamlain.payment.billing-setting.year']()}
        </span>
      </p>
    </div>
  );
};

interface BillingContractChange {
  id: string;
  changeKind: string;
  targetType: string;
}

function getContractChangeLabel(
  change: BillingContractChange,
  language?: string
): string {
  const english = language?.startsWith('en');
  if (change.targetType === 'seat_addon') {
    return english ? 'Extra seats' : 'Zusatz-Sitze';
  }
  if (change.targetType === 'credit_subscription') {
    return english ? 'Credit subscription' : 'Credit-Abo';
  }
  if (change.targetType === 'plan_subscription') {
    return english ? 'Plan change' : 'Planänderung';
  }
  return english ? 'Contract change' : 'Vertragsänderung';
}
